import React from "react";

/**
 * Implémente le langage de tags Shoggoth observé dans un vrai project.json
 * (ex: le fichier "Cyclopean-Foundation"), plus large que ce que documente
 * la doc publique consultée. Liste non exhaustive mais couvrant les cas
 * fréquents :
 *
 *   <b>...</b> <i>...</i> <t>...</t>              gras / italique / trait
 *   <blockquote>...</blockquote>                   citation narrative
 *   <center>...</center>                            texte centré
 *   <name>                                          substitué par le nom de la carte/face
 *   <bullet>                                        puce de liste ("• ")
 *   <for> <rev> <obj> <spawn> <pre>                 préfixes de mot-clé ("Forced –", etc.)
 *   <action> <free> <reaction>                       symboles d'action
 *   <skull> <cultist> <tablet> <elder_thing>          jetons de chaos
 *   <elder_sign> <auto_fail>                          jetons de chaos spéciaux
 *   <willpower> <intellect> <combat> <agility>        icônes de stat
 *   <guardian> <seeker> <rogue> <mystic> <survivor>   icônes de classe
 *   <unique>                                          icône "carte unique"
 *   <per>                                             "par investigateur"
 *   <dash>                                            tiret cadratin (valeur absente)
 *   <br>                                              retour à la ligne explicite
 *   --  / ---                                          – (en dash) / — (em dash)
 *   \n                                                 retour à la ligne (préservé en CSS)
 *   <image "..."> / <font ...>...</font>              non supportés, retirés proprement
 *
 * On ne vise pas un rendu pixel-perfect des icônes (ce rôle est réservé au
 * rendu réel produit par le conteneur Shoggoth, cf. RenderPreview), mais un
 * rendu texte fidèle et lisible pour la review/traduction au quotidien.
 */

const SELF_CLOSING_ICON_TAGS = new Set([
  "action",
  "free",
  "reaction",
  "skull",
  "cultist",
  "tablet",
  "elder_thing",
  "elder_sign",
  "auto_fail",
  "willpower",
  "intellect",
  "combat",
  "agility",
  "guardian",
  "seeker",
  "rogue",
  "mystic",
  "survivor",
  "unique",
]);

const EXPANSION_TAGS: Record<string, string> = {
  for: "Forced –",
  rev: "Revelation –",
  prey: "Prey –",
  pre: "Prey –",
  obj: "Objective –",
  spawn: "Spawn –",
  per: "per investigator",
  dash: "—",
  bullet: "•",
};

const ICON_LABELS: Record<string, string> = {
  action: "▶",
  free: "⚡",
  reaction: "↺",
  skull: "☠",
  cultist: "☺",
  tablet: "▦",
  elder_thing: "☣",
  elder_sign: "✦",
  auto_fail: "✕",
  willpower: "◐",
  intellect: "◑",
  combat: "◒",
  agility: "◓",
  guardian: "🛡",
  seeker: "🔍",
  rogue: "🗡",
  mystic: "✨",
  survivor: "❤",
  unique: "◆",
};

function expandDashesAndBreaks(text: string): string {
  return text.replace(/---/g, "—").replace(/--/g, "–");
}

/** Retire les directives non supportées (images/police embarquées), substitue
 * <name> par le nom effectif de la carte/face, et referme les <center>
 * laissés ouverts jusqu'à la fin du texte — un motif fréquent dans les vraies
 * données Shoggoth (ex: "...<center><b>Victory 1.</b>" sans </center>). */
function preprocess(rawText: string, displayName?: string): string {
  let text = rawText;
  text = text.replace(/<image\s+[^>]*>/gi, ""); // <image "chemin" taille>
  text = text.replace(/<font\s+[^>]*>/gi, "").replace(/<\/font>/gi, "");
  if (displayName) text = text.split("<name>").join(displayName);

  const opens = (text.match(/<center>/g) ?? []).length;
  const closes = (text.match(/<\/center>/g) ?? []).length;
  if (opens > closes) text += "</center>".repeat(opens - closes);

  return text;
}

const TAG_REGEX =
  /<(b|i|t|blockquote|center)>([\s\S]*?)<\/\1>|<(action|free|reaction|skull|cultist|tablet|elder_thing|elder_sign|auto_fail|willpower|intellect|combat|agility|guardian|seeker|rogue|mystic|survivor|unique|for|rev|prey|pre|obj|spawn|per|dash|bullet|br)\s*\/?>/g;

let keyCounter = 0;

/** Parse une chaîne de texte de carte Shoggoth en noeuds React.
 * `displayName` : nom à substituer aux occurrences du tag <name>. */
export function parseCardText(
  rawText: string | undefined | null,
  displayName?: string
): React.ReactNode[] {
  if (!rawText) return [];
  const text = preprocess(rawText, displayName);
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  TAG_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TAG_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(expandDashesAndBreaks(text.slice(lastIndex, match.index)));
    }

    const [, pairedTag, innerContent, selfTag] = match;

    if (pairedTag === "b") {
      nodes.push(<strong key={`k${keyCounter++}`}>{parseCardText(innerContent, displayName)}</strong>);
    } else if (pairedTag === "i") {
      nodes.push(<em key={`k${keyCounter++}`}>{parseCardText(innerContent, displayName)}</em>);
    } else if (pairedTag === "t") {
      nodes.push(
        <span key={`k${keyCounter++}`} className="card-trait">
          {parseCardText(innerContent, displayName)}
        </span>
      );
    } else if (pairedTag === "blockquote") {
      nodes.push(
        <blockquote key={`k${keyCounter++}`} className="card-blockquote">
          {parseCardText(innerContent, displayName)}
        </blockquote>
      );
    } else if (pairedTag === "center") {
      nodes.push(
        <div key={`k${keyCounter++}`} className="card-center">
          {parseCardText(innerContent, displayName)}
        </div>
      );
    } else if (selfTag === "br") {
      nodes.push(<br key={`k${keyCounter++}`} />);
    } else if (selfTag && EXPANSION_TAGS[selfTag]) {
      nodes.push(EXPANSION_TAGS[selfTag]);
    } else if (selfTag && SELF_CLOSING_ICON_TAGS.has(selfTag)) {
      nodes.push(
        <span key={`k${keyCounter++}`} className={`card-icon card-icon--${selfTag}`} title={selfTag}>
          {ICON_LABELS[selfTag] ?? selfTag}
        </span>
      );
    }

    lastIndex = TAG_REGEX.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(expandDashesAndBreaks(text.slice(lastIndex)));
  }

  return nodes;
}
